<template>
  <section class="py-24 max-w-7xl mx-auto px-6" id="menu" aria-labelledby="menu-heading-t4">
    <div class="flex justify-between items-end mb-12">
      <div>
        <h2 id="menu-heading-t4" class="text-3xl font-bold tracking-tight mb-2 text-slate-900 dark:text-slate-100">{{ sectionTitle || $t('public.viewMenu') }}</h2>
        <p class="text-slate-600 dark:text-slate-400 text-sm">{{ $t('public.t3ExquisiteSelections') }}</p>
      </div>
    </div>
    <p v-if="!displayGroups.length" class="text-slate-600 dark:text-slate-400 text-center py-12">{{ emptyMessage || $t('public.noMenuItems') }}</p>
    <template v-else>
      <div class="flex justify-center gap-4 sm:gap-8 mb-12 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        <button
          v-for="(group, gIdx) in displayGroups"
          :key="gIdx"
          type="button"
          class="pb-4 border-b-2 shrink-0 font-bold text-sm sm:text-base min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-t transition-colors"
          :class="activeGroupIndex === gIdx ? 'border-[var(--rms-t4-primary,#ec5b13)] text-[var(--rms-t4-primary,#ec5b13)]' : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'"
          :style="activeGroupIndex === gIdx ? {} : {}"
          @click="onGroupTabClick(group, gIdx)"
        >
          {{ group.category_name }}
        </button>
      </div>
      <p
        v-if="activeGroup?.category_description"
        class="text-slate-600 dark:text-slate-400 text-center text-sm max-w-2xl mx-auto mb-8 leading-relaxed"
      >
        {{ activeGroup.category_description }}
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <template v-if="activeGroup && activeGroup.items.length">
          <article
            v-for="item in activeGroup.items"
            :key="item.uuid"
            class="group relative p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-[var(--rms-t4-primary,#ec5b13)]/50 transition-colors"
            :class="{ 'rms-item-unavailable': !isItemClickable(item, activeGroup) }"
          >
            <PublicMenuItemOwnerEditFab
              v-if="showOwnerEditFab"
              :item-uuid="item.catalog_item_uuid || item.uuid"
              :visible="showOwnerEditFab"
            />
            <!-- Items with variants: header opens detail modal; dropdowns update price -->
            <template v-if="item.type === 'with_variants'">
              <button
                type="button"
                data-testid="public-menu-item"
                class="rms-t4-menu-item w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] focus-visible:ring-offset-2 rounded-lg min-h-[44px] py-0"
                :aria-disabled="!isItemClickable(item, activeGroup)"
                @click="isItemClickable(item, activeGroup) ? onVariantHeaderClick(item, activeGroup.category_name, $event) : showUnavailableToast(activeGroup, item, now)"
              >
                <img
                  v-if="itemCardDisplayImageUrl(item)"
                  :src="itemCardDisplayImageUrl(item)"
                  :alt="item.name || 'Untitled'"
                  loading="lazy"
                  class="w-12 h-12 sm:w-14 sm:h-14 shrink-0 aspect-square object-cover rounded-xl mb-4 border border-slate-100 dark:border-slate-700"
                >
                <span
                  v-else
                  class="inline-flex w-12 h-12 sm:w-14 sm:h-14 shrink-0 items-center justify-center rounded-xl mb-4 text-2xl border border-slate-100 dark:border-slate-700"
                  :style="{ color: 'var(--rms-t4-primary,#ec5b13)', backgroundColor: 'rgba(236,91,19,0.1)' }"
                >
                  <span class="material-symbols-outlined">restaurant</span>
                </span>
                <h3 class="text-xl font-bold text-slate-900 dark:text-slate-100 group-hover:opacity-90 transition-opacity mb-2" :class="item.is_available === false ? 'text-slate-500' : ''">
                  {{ item.name || 'Untitled' }}
                </h3>
                <p v-if="item.description" class="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">{{ item.description }}</p>
                <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mb-2">
                  <span
                    v-for="tag in itemTags(item)"
                    :key="tag.uuid"
                    class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                    :style="tagStyle(tag)"
                    :title="tag.text"
                    role="img"
                    :aria-label="tag.text"
                    tabindex="0"
                  >
                    <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                    <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                  </span>
                </span>
              </button>
              <div
                v-if="variantSkus(item).length && variantOptionGroups(item).length"
                class="mt-4 space-y-3"
                @click.stop
              >
                <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span
                    v-if="item.is_available !== false && isVariantSkuPubliclyOrderable(resolvedVariantSku(item)) && resolvedVariantSku(item)?.price != null"
                    class="font-bold text-lg"
                    :style="{ color: 'var(--rms-t4-primary,#ec5b13)' }"
                  >
                    {{ formatPrice(resolvedVariantSku(item).price) }}
                  </span>
                  <span v-else-if="item.is_available === false" class="text-slate-500 text-sm">{{ $t('public.notAvailable') }}</span>
                </div>
                <div
                  v-for="optGroup in variantOptionGroups(item)"
                  :key="`${item.uuid}-${optGroup.name}`"
                  class="space-y-1.5"
                >
                  <label
                    class="block text-sm font-semibold text-slate-700 dark:text-slate-300"
                    :for="variantSelectId('rms-t4', item, optGroup.name)"
                  >
                    {{ optGroup.name || $t('public.option') }}
                  </label>
                  <select
                    :id="variantSelectId('rms-t4', item, optGroup.name)"
                    class="rms-t4-variant-select w-full min-h-[44px] rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                    :value="variantSelectionValue(item, optGroup.name)"
                    :disabled="!isItemClickable(item, activeGroup)"
                    :aria-label="`${optGroup.name || $t('public.option')}: ${item.name || ''}`"
                    @change="onVariantGroupChange(item, optGroup.name, $event.target.value)"
                  >
                    <option
                      v-for="val in (optGroup.values || [])"
                      :key="String(val)"
                      :value="val"
                      :disabled="!isVariantGroupValueOrderable(item, optGroup.name, val)"
                    >
                      {{ val }}{{ !isVariantGroupValueOrderable(item, optGroup.name, val) ? ' (' + $t('public.variantOptionNotAvailable') + ')' : '' }}
                    </option>
                  </select>
                </div>
              </div>
              <p v-else-if="item.type === 'with_variants' && variantSkus(item).length && !variantOptionGroups(item).length" class="text-slate-500 text-sm mt-2">
                {{ variantOptionGroupsSummary(item) }}
              </p>
            </template>
            <!-- Standard / combo items -->
            <button
              v-else
              type="button"
              data-testid="public-menu-item"
              class="rms-t4-menu-item w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] focus-visible:ring-offset-2 rounded-lg min-h-[44px] py-0"
              :aria-disabled="!isItemClickable(item, activeGroup)"
              @click="isItemClickable(item, activeGroup) ? onItemClick(item, activeGroup.category_name, $event) : showUnavailableToast(activeGroup, item, now)"
            >
              <img
                v-if="itemCardDisplayImageUrl(item)"
                :src="itemCardDisplayImageUrl(item)"
                :alt="item.name || 'Untitled'"
                loading="lazy"
                class="w-12 h-12 sm:w-14 sm:h-14 shrink-0 aspect-square object-cover rounded-xl mb-4 border border-slate-100 dark:border-slate-700"
              >
              <span
                v-else
                class="inline-flex w-12 h-12 sm:w-14 sm:h-14 shrink-0 items-center justify-center rounded-xl mb-4 text-2xl border border-slate-100 dark:border-slate-700"
                :style="{ color: 'var(--rms-t4-primary,#ec5b13)', backgroundColor: 'rgba(236,91,19,0.1)' }"
              >
                <span class="material-symbols-outlined">restaurant</span>
              </span>
              <h3 class="text-xl font-bold text-slate-900 dark:text-slate-100 group-hover:opacity-90 transition-opacity mb-2" :class="item.is_available === false ? 'text-slate-500' : ''">
                {{ item.name || 'Untitled' }}
              </h3>
              <p v-if="item.description" class="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">{{ item.description }}</p>
              <span v-if="itemTags(item).length" class="flex flex-wrap items-center gap-1.5 mb-2">
                <span
                  v-for="tag in itemTags(item)"
                  :key="tag.uuid"
                  class="rms-public-tag inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium min-h-[28px] transition-[opacity,background-color] duration-200"
                  :style="tagStyle(tag)"
                  :title="tag.text"
                  role="img"
                  :aria-label="tag.text"
                  tabindex="0"
                >
                  <span class="material-symbols-outlined text-sm shrink-0" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                  <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                </span>
              </span>
              <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span
                  v-if="item.is_available !== false && itemPrice(item) != null"
                  class="font-bold text-lg"
                  :style="{ color: 'var(--rms-t4-primary,#ec5b13)' }"
                >
                  {{ formatPrice(itemPrice(item)) }}
                </span>
                <span v-else-if="item.is_available === false" class="text-slate-500 text-sm">{{ $t('public.notAvailable') }}</span>
              </div>
              <ul v-if="item.type === 'combo' && comboEntries(item).length" class="mt-2 list-none pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-0.5 text-slate-500 text-xs sm:text-sm" aria-label="Combo contents">
                <li v-for="(entry, eIdx) in comboEntries(item)" :key="eIdx">
                  {{ entry.name }} × {{ entry.quantity }}{{ entry.modifier_label ? ` (${entry.modifier_label})` : '' }}
                </li>
              </ul>
            </button>
          </article>
        </template>
        <p v-else class="col-span-full text-slate-600 dark:text-slate-400 text-center py-8">{{ emptyMessage || $t('public.noMenuItems') }}</p>
      </div>
    </template>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { usePublicUnavailableToast } from '@/composables/usePublicUnavailableToast'
import { usePublicMenuDisplay } from '@/composables/usePublicMenuDisplay'
import { usePublicMenuVariantSelect } from '@/composables/usePublicMenuVariantSelect'
import PublicMenuItemOwnerEditFab from '@/components/public/PublicMenuItemOwnerEditFab.vue'
import { isAvailableNow } from '@/utils/availability'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku'

const emit = defineEmits(['select-item'])
const { showUnavailableToast, showUnavailableCategoryToast } = usePublicUnavailableToast()

const props = defineProps({
  menuGroups: { type: Array, default: () => [] },
  menuItems: { type: Array, default: () => [] },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
  sectionTitle: { type: String, default: '' },
  emptyMessage: { type: String, default: '' },
  showOwnerEditFab: { type: Boolean, default: false },
})

const {
  displayGroups,
  formatPrice,
  itemPrice,
  itemTags,
  comboEntries,
  variantOptionGroupsSummary,
  variantOptionGroups,
  variantSkus,
  itemCardImageUrl,
} = usePublicMenuDisplay(props)

const activeGroupIndex = ref(0)
const activeGroup = computed(() => displayGroups.value[activeGroupIndex.value] ?? null)

const {
  variantSelectId,
  resolvedVariantSku,
  variantSelectionValue,
  onVariantGroupChange,
  isVariantGroupValueOrderable,
} = usePublicMenuVariantSelect({
  displayGroups,
  variantSkus,
  variantOptionGroups,
})

function tagStyle(tag) {
  const hex = tag?.color || '#64748b'
  return {
    '--tag-bg': `${hex}20`,
    '--tag-fg': hex,
  }
}

function onItemClick(item, categoryName, event) {
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: null,
    trigger: event?.currentTarget ?? null,
  })
}

function onVariantHeaderClick(item, categoryName, event) {
  const sku = resolvedVariantSku(item)
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku: sku || null,
    trigger: event?.currentTarget ?? null,
  })
}

const now = ref(new Date())

function categoryUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function itemUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function isItemClickable(item, group) {
  if (!item || !group) return false
  if (categoryUnavailableNow(group.availability)) return false
  if (item.is_available === false) return false
  if (itemUnavailableNow(item.availability)) return false
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    if (!sku || !isVariantSkuPubliclyOrderable(sku)) return false
  }
  return true
}

function onGroupTabClick(group, gIdx) {
  activeGroupIndex.value = gIdx
  if (categoryUnavailableNow(group?.availability)) {
    showUnavailableCategoryToast(group, now)
  }
}

function itemCardDisplayImageUrl(item) {
  if (!item) return null
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    return sku?.image_url ?? sku?.imageUrl ?? itemCardImageUrl(item)
  }
  return itemCardImageUrl(item)
}
</script>

<style scoped>
.rms-public-tag {
  background-color: var(--tag-bg);
}
.rms-public-tag:hover {
  opacity: 0.9;
}
.rms-t4-menu-item {
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  display: block;
}
.rms-t4-variant-select {
  appearance: auto;
}
.rms-item-unavailable {
  filter: grayscale(0.7) blur(0.5px);
  opacity: 0.7;
}
.rms-item-unavailable .rms-t4-menu-item {
  cursor: not-allowed;
}
</style>
